'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import dayjs from '@/common/dayjs';
import { OFFICIAL_URL } from '@/common/constant';
import { sendGTMEvent } from '@/common/util';
import missionClient from '@/common/apiClient/missionClient';
import useUrlStore from '@/store/useUrlStore';
import useUserStore from '@/store/useUserStore';
import useModalStore from '@/store/useModalStore';
import { sendInteractMsgToOther } from '@/common/socketFunctions';
import useToastStore from '@/store/useToastStore';

export type MissionType = 'special' | 'daily' | 'longterm' | 'weekly';

export interface Mission {
    id: number;
    name: string;
    condition: string;
    reward: string;
    rewardAmount: number;
    period: string;
    type: string;
    status: string;
    term: any;
    category: MissionType;
    // 任務期間（後端 mission-report 回傳，未設定為 null）；限時任務用來顯示結束時間
    beginTime?: string | null;
    endTime?: string | null;
}

// 達成條件類型分兩類：
//   事件型（daily / special）：browse / like / comment / share / post / post_article，導向指定內容去完成行為
//   狀態型（longterm）：導向對應設定頁去完成帳號狀態
type MissionStateType = 'profile' | 'app_login';
interface MissionTerm {
    contentId?: number;
    contentType?: string;
    pageKey?: string; // contentType='page' 時的目標頁 key
    url?: string; // 後端僅在有指定內容、或條件類型自帶導向網址(follow_social/pre_registration/app_checkin)時回傳
    tagName?: string;
    tagId?: number;
    hubId?: number; // type='post_article' 時的指定專板
    platform?: string; // type='follow_social' 時的社群平台（url 為該平台官方帳號）
    preRegistrationId?: number; // type='pre_registration' 時的預熱活動（url 為導向網址）
    activityId?: number; // type='vote' 時的投票活動（url 為官網活動頁路徑 /event/{slug}）
    slug?: string; // type='vote' 時的活動 slug（後端由 act_activity 反查帶下來）
}

// 追蹤社群平台：點連結即算完成，網址由後端 term.url 帶下來（白名單，不由前端寫死）
const isFollowSocialMission = (type: string) => type === 'follow_social';

// 預熱活動：達成與否看報名紀錄，不是點擊型任務；
// term.url 是後台設定的 OneLink（有 App 開 App 內活動頁、無 App 導商店）
const isPreRegistrationMission = (type: string) => type === 'pre_registration';

// 投票活動：達成與否看 idol_ticket 的投票紀錄，不是點擊型任務；
// term.url 是官網活動頁的相對路徑（/event/{slug}），由後端依活動 slug 組出
const isVoteMission = (type: string) => type === 'vote';

// APP 每日簽到：達成看的是 App 的簽到紀錄，任務本身不發星塵（達成後直接是 done），
// term.url 是後台設定的導向網址（預設為 App 的通用 OneLink）
const isAppCheckinMission = (type: string) => type === 'app_checkin';

// 需另開分頁的外部連結任務（App 下載商店、社群官方帳號、預熱活動 OneLink、App 簽到）
const isExternalLinkMission = (type: string) =>
    type === 'app_login' ||
    isFollowSocialMission(type) ||
    isPreRegistrationMission(type) ||
    isAppCheckinMission(type);

interface Urls {
    forumUrl: string;
    web01Url: string;
    hubUrl: string;
    memberUrl: string;
    h18Url: string;
}

const CONTENT_URL_BUILDERS: Record<string, (urls: Urls, url: string, id: number) => string> = {
    article: (urls, url, id) => `${urls.hubUrl}${url}`,
    news: (urls, url, id) => `${urls.web01Url}${url}`,
    post: (urls, url) => `${urls.forumUrl}${url}`,
    // 之後有新 contentType 就加一行
};
// 功能頁面任務(contentType='page')的落地頁；key 對應後端 MISSION_PAGE_TARGET
const PAGE_TARGET_URLS: Record<string, (urls: Urls) => string> = {
    main_home: (urls) => urls.web01Url,
    h18_home: (urls) => urls.h18Url,
    exchange_home: () => '/', // 兌換所首頁就是本站
    forum_topics: (urls) => `${urls.forumUrl}/topics`,
};
// 任務未指定內容時的落地頁：各站首頁即該類型的列表頁
const CONTENT_LIST_URLS: Record<string, (urls: Urls) => string> = {
    article: (urls) => urls.hubUrl,
    news: (urls) => urls.web01Url,
    post: (urls) => urls.forumUrl,
};
// 長期任務（狀態型）的落地頁：信箱綁定 / 出生日期 / MBTI 都在會員站個人檔案頁；
// 下載 App 走 OneLink，由它依裝置導向 App Store 或 Google Play
const STATE_MISSION_URLS: Record<MissionStateType, (urls: Urls) => string> = {
    profile: (urls) => `${urls.memberUrl}/profile`,
    app_login: () => OFFICIAL_URL.APP,
};

const isStateMission = (type: string): type is MissionStateType => type in STATE_MISSION_URLS;

function getMissionLink(type: string, term: MissionTerm, urls: Urls): string {
    // 追蹤社群平台：導向該平台官方帳號（term.url 由後端白名單回填）
    // （設定不全時退回本站，不要誤導到其他外站）
    if (isFollowSocialMission(type)) return term?.url || '/';
    // 預熱活動：導向後台設定的 OneLink（未設定時退回 App 下載，不誤導到其他站）
    if (isPreRegistrationMission(type)) return term?.url || OFFICIAL_URL.APP;
    // APP 每日簽到：導向後台設定的網址（後端存檔時留空會補 App OneLink，此處為舊資料防線）
    if (isAppCheckinMission(type)) return term?.url || OFFICIAL_URL.APP;
    // 投票活動：導向官網的活動頁（term.url 是 /event/{slug} 相對路徑，接上官網網域）
    if (isVoteMission(type)) {
        return term?.url ? `${urls.web01Url}${term.url}` : urls.web01Url;
    }
    if (isStateMission(type)) return STATE_MISSION_URLS[type](urls);
    // post 類任務是「去發文」,沒有目標內容;未指定標籤時不帶 query
    if (type === 'post') {
        return term?.tagId
            ? `${urls.forumUrl}/post?tag=${term.tagName}&tagId=${term.tagId}`
            : `${urls.forumUrl}/post`;
    }
    // 發佈專板貼文任務是「去專板發文」,沒有目標內容;
    // 有指定專板時後端會把 term.url 補成 /post?hubSlug=xxx，發文頁會預選該專板
    if (type === 'post_article') {
        return term?.url ? `${urls.hubUrl}${term.url}` : `${urls.hubUrl}/post`;
    }
    // 功能頁面任務：導向該站台的目標頁（term 沒有 contentId，不走 CONTENT_URL_BUILDERS）
    if (term?.contentType === 'page') {
        const buildPage = term.pageKey ? PAGE_TARGET_URLS[term.pageKey] : undefined;
        return buildPage ? buildPage(urls) : urls.web01Url;
    }
    const build = term?.contentType ? CONTENT_URL_BUILDERS[term.contentType] : undefined;
    if (build && term.contentId && term.url) return build(urls, term.url, term.contentId);
    // 未指定內容:導向該內容類型的列表頁
    const listUrl = term?.contentType ? CONTENT_LIST_URLS[term.contentType] : undefined;
    return listUrl ? listUrl(urls) : urls.web01Url;
}
interface MissionBlockProps {
    mission: Mission;
    onClaimed?: (id: number) => void;
}

export default function MissionBlock({ mission, onClaimed }: MissionBlockProps) {
    const {
        id,
        name,
        type,
        category,
        condition,
        term,
        reward,
        endTime,
        rewardAmount,
        status,
        period,
    } = mission;
    const urls = useUrlStore();
    const { user, setUser } = useUserStore();
    const openModal = useModalStore((state) => state.openModal);
    const { openToast } = useToastStore();
    const [isClaimed, setIsClaimed] = useState(mission.status === 'done' || false);
    const [isClaiming, setIsClaiming] = useState(false);
    // 追蹤社群平台任務點擊後即達成，就地切成可領取狀態（不必等下次進頁面重新取報表）
    const [clickCompleted, setClickCompleted] = useState(false);
    const isDrawable = status === 'drawable' || clickCompleted;

    // console.log(mission);

    // 未登入也看得到任務清單（mission-report 支援 uid=null），但任務進度要登入才會累積，
    // 故點「去完成」不導頁，改開登入彈窗（登入後會導回本頁）。
    // app_login 的「去下載」例外：未登入也能先下載 App，到 App 內登入即算達成。
    const handleMissionLinkClick = async (e: React.MouseEvent) => {
        if (!user?.uid && type !== 'app_login') {
            e.preventDefault();
            openModal('LOGIN');
            return;
        }

        // 所有任務都記錄「去完成」點擊（供點擊率報表）。連結照常導頁（不 preventDefault）。
        // 未登入時（只會是 app_login 的「去下載」）無從歸戶，不記錄。
        if (!user?.uid) return;

        // 一般任務：純埋點，fire-and-forget——不 await、失敗只記 log，絕不能擋住使用者導頁
        if (!isFollowSocialMission(type)) {
            missionClient
                .recordMissionClick(id)
                .catch((error) => console.error('記錄任務點擊失敗', error));
            return;
        }

        // 追蹤社群平台：點擊即算完成，寫入成功後就地把按鈕換成「領取星塵」，
        // 使用者不必重整頁面
        try {
            const result = await missionClient.recordMissionClick(id);
            setClickCompleted(true);

            // GTM 埋點：任務從未完成變為已完成（follow_social 點擊即完成）
            (result?.newlyCompletedMissions ?? []).forEach((m: { id: number; name: string }) => {
                sendGTMEvent({ event: 'complete_tasks', missionId: m.id, missionName: m.name });
            });
        } catch (error) {
            console.error('記錄任務點擊失敗', error);
        }
    };

    // 點擊領取星塵
    const handleClaim = async () => {
        if (isClaiming) return;

        try {
            setIsClaiming(true);
            const { isFirstClaim = false, newlyCompletedMissions = [] } =
                await missionClient.claimMission(id);
            setIsClaimed(true);
            // 通知上層（MissionSection）把這筆移到清單最後面
            onClaimed?.(id);

            // GTM 埋點：第一次在平台完成任務並點擊【領取星塵】時觸發
            if (isFirstClaim)
                sendGTMEvent({ event: 'claim_first_time', missionId: id, missionName: name });

            // GTM 埋點：重新組報表時若同時有其他任務剛完成，一併補上 complete_tasks
            (newlyCompletedMissions ?? []).forEach((m: { id: number; name: string }) => {
                sendGTMEvent({ event: 'complete_tasks', missionId: m.id, missionName: m.name });
            });

            // GTM 埋點：每一次完成任務並成功領取星塵都算一次
            sendGTMEvent({
                event: 'claim_stardust',
                missionId: id,
                missionName: name,
            });

            // 更新星塵持有數(側欄 AsideDustInfo 讀 store 會自動更新)
            const newBalance = (user?.balance ?? 0) + rewardAmount;
            setUser({ balance: newBalance });
            openToast(`獲得`, {
                icon: '/images/icon/icon_point.svg',
                position: 'top',
                duration: 5000,
                text: `+${rewardAmount}`,
            });
            // 通知其他裝置同步餘額
            await sendInteractMsgToOther({
                uid: user?.uid,
                userChange: { balance: newBalance },
            });
        } catch (error) {
            console.error('領取任務點數失敗', error);
        } finally {
            setIsClaiming(false);
        }
    };

    return (
        <>
            <div
                className={`border border-black1 px-3 md:px-6 py-3 rounded-md min-h-24 flex flex-wrap xxs:flex-nowrap items-center justify-between gap-4 transition-colors
                ${isClaimed ? 'bg-black1' : 'bg-white'}`}
            >
                <div className={`space-y-0.5 min-w-0 trans ${isClaimed ? 'opacity-60' : ''}`}>
                    <h4 className="text-btn3b text-jblack line-clamp-2 xs:line-clamp-none">
                        {name}
                    </h4>
                    <div className="flex items-center gap-2 text-bd3n text-black5">
                        <span className="block">{condition}</span>
                        <div className="flex mr-2 self-start">
                            <Image
                                src="/images/icon/icon_point.svg"
                                width={16}
                                height={16}
                                alt="星塵"
                            />
                            <span className="ml-1">+{rewardAmount}</span>
                        </div>
                    </div>
                    {/* 限時任務才顯示結束時間（達成判定期間為開始～結束，逾期未領即失效） */}
                    {category === 'special' && endTime && (
                        <p className="text-bd3n text-cobalt3">
                            {dayjs(endTime).format('YYYY.MM.DD HH:mm')} 結束
                        </p>
                    )}
                </div>

                {status === 'done' || isClaimed ? (
                    <button
                        type="button"
                        disabled
                        className="shrink-0 text-btn1 text-[#fff] bg-vPurple w-20 md:w-31.25 py-3 rounded-full cursor-not-allowed opacity-60"
                    >
                        已完成
                    </button>
                ) : isDrawable ? (
                    <button
                        type="button"
                        onClick={handleClaim}
                        disabled={isClaiming}
                        className="shrink-0 text-btn1 text-[#fff] bg-bou w-20 md:w-31.25 py-3 rounded-full cp disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {isClaimed ? '領取中' : '領取星塵'}
                    </button>
                ) : (
                    <Link
                        href={getMissionLink(type, term, urls)}
                        onClick={handleMissionLinkClick}
                        // App 下載商店、社群官方帳號、預熱活動 OneLink 都是外部連結，
                        // 另開分頁避免使用者離開兌換所
                        target={isExternalLinkMission(type) ? '_blank' : undefined}
                        rel={isExternalLinkMission(type) ? 'noopener noreferrer' : undefined}
                        className="shrink-0 text-btn1 text-[#fff] bg-vPurple w-20 md:w-31.25 py-3 rounded-full text-center"
                    >
                        {type === 'app_login'
                            ? '去下載'
                            : isFollowSocialMission(type)
                              ? '去追蹤'
                              : isPreRegistrationMission(type)
                                ? '去報名'
                                : isAppCheckinMission(type)
                                  ? '去簽到'
                                  : isVoteMission(type)
                                    ? '去投票'
                                    : '去完成'}
                    </Link>
                )}
            </div>
        </>
    );
}
